// Policies > Voicemail drops (ADR 0022, DESIGN.md "Voicemail drops"): the company switch, the
// notice and its acknowledgment, the lines rule, the state rules shared with Parallel dialing,
// and the records: drops, refusals and dismissed message warnings. Admin only.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Checkbox, Table, TBody, Td, THead, Th, Tr } from "@vd/ui";
import { ExternalLink } from "lucide-react";
import { useState } from "react";
import { errorMessage } from "../../../api/client";
import { pdial, pdialKeys, pdialQuery, type StateRule } from "../../../api/pdial";
import {
  BEEP,
  clockMs,
  LINE,
  type Notice,
  RESULT,
  recordsCsvHref,
  type VmDropsAdmin,
  vmDismissalsQuery,
  vmDrops,
  vmDropsQuery,
  vmKeys,
  vmRecordsQuery,
} from "../../../api/vmdrops";
import { requireAdmin } from "../../../auth/useMe";
import { toast } from "../../../components/toast";
import {
  Badge,
  Button,
  Card,
  Dialog,
  ErrorState,
  HelpTip,
  Input,
  Loading,
  PageHeader,
  Switch,
  Tabs,
} from "../../../components/ui";
import { fmtDate, fmtDateTime } from "../../../lib/format";

export const Route = createFileRoute("/admin/calling/voicemail-drops")({
  staticData: { crumb: { label: "Voicemail drops", parents: [] } },
  beforeLoad: requireAdmin,
  loader: ({ context }) => context.queryClient.prefetchQuery(vmDropsQuery),
  component: VoicemailDropsPage,
});

type Tab = "setup" | "rules" | "records";

function VoicemailDropsPage() {
  const q = useQuery(vmDropsQuery);
  const [tab, setTab] = useState<Tab>("setup");
  return (
    <>
      <PageHeader title="Voicemail drops" />
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          <Banners s={q.data} />
          <Tabs<Tab>
            label="Voicemail drops sections"
            value={tab}
            onChange={setTab}
            tabs={[
              { value: "setup", label: "Setup" },
              { value: "rules", label: "State rules" },
              { value: "records", label: "Records" },
            ]}
          />
          {tab === "setup" ? <Setup s={q.data} /> : null}
          {tab === "rules" ? <RulesTab s={q.data} /> : null}
          {tab === "records" ? <RecordsTab s={q.data} /> : null}
        </>
      )}
    </>
  );
}

/** Drops are paused: an acknowledgment exists but of an older notice. */
function paused(s: VmDropsAdmin): boolean {
  return !s.ack_current && !!s.acknowledgment;
}

function Banners({ s }: { s: VmDropsAdmin }) {
  const notes: { tone: "warn" | "danger"; text: string }[] = [];
  if (paused(s))
    notes.push({ tone: "danger", text: "Drops are paused until an admin agrees to the updated notice." });
  if (!s.enabled)
    notes.push({
      tone: "warn",
      text: "Voicemail drops are off. Reps don't see Leave voicemail until an admin agrees to the notice and turns them on.",
    });
  if (notes.length === 0) return null;
  return (
    <div className="mb-4 flex flex-col gap-2">
      {notes.map((n) => (
        <div
          key={n.text}
          role="status"
          className={`rounded-md px-3 py-2 text-body ${n.tone === "danger" ? "bg-danger-tint text-danger-ink" : "bg-warning-tint text-warning-ink"}`}
        >
          {n.text}
        </div>
      ))}
    </div>
  );
}

function Row({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <li className="grid items-start gap-x-4 gap-y-2 px-4 py-3 md:grid-cols-[minmax(0,240px)_minmax(0,1fr)]">
      <div className="flex items-center gap-1.5 pt-1">
        <span className="font-medium text-ui">{label}</span>
        {help ? <HelpTip text={help} /> : null}
      </div>
      <div className="min-w-0">{children}</div>
    </li>
  );
}

// Setup ---------------------------------------------------------------------------------------

function Setup({ s }: { s: VmDropsAdmin }) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (enabled: boolean) => vmDrops.setEnabled(enabled),
    onSuccess: (v) => {
      qc.setQueryData(vmKeys.admin, v);
      toast(v.enabled ? "Voicemail drops are on." : "Voicemail drops are off.");
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const agreed = s.ack_current;
  const status = s.enabled
    ? `On since ${s.enabled_at ? fmtDate(s.enabled_at) : "now"}${s.enabled_by_name ? ` by ${s.enabled_by_name}` : ""}`
    : agreed
      ? "Off"
      : "Read the notice and agree first.";
  return (
    <div className="flex flex-col gap-4">
      <Card title="Voicemail drops">
        <ul className="divide-y divide-hairline">
          <Row
            label="Turn on for the company"
            help="Reps then see Leave voicemail on calls to business lines the rules allow."
          >
            <div className="flex flex-wrap items-center gap-3">
              <Switch
                label="Voicemail drops"
                checked={s.enabled}
                disabled={save.isPending || (!s.enabled && !agreed)}
                onChange={(v) => save.mutate(v)}
              />
              <span className="text-ui text-ink-2">{status}</span>
            </div>
            {save.error ? (
              <p role="alert" className="mt-1 text-caption text-danger-ink">
                {errorMessage(save.error)}
              </p>
            ) : null}
          </Row>
        </ul>
      </Card>
      <NoticeCard s={s} />
      <Card title="Rules">
        <ul className="divide-y divide-hairline">
          <Row label="Lines">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-body">
              <dt className="text-ink-2">Cell phones</dt>
              <dd>Not allowed</dd>
              <dt className="text-ink-2">Unknown lines</dt>
              <dd>Not allowed</dd>
              <dt className="text-ink-2">VoIP</dt>
              <dd>
                {s.voip_as_cell ? "Cell phone" : "Business line"}
                <span className="block text-caption text-ink-3">
                  Follows Treat VoIP numbers as cells on Parallel dialing.
                </span>
              </dd>
            </dl>
          </Row>
          <Row label="Message review">
            <p className="text-body">
              Every message is reviewed when it is saved: the company name and callback number, and an AI
              reading of what it says. Whoever keeps a message re-records it or dismisses the warnings, and
              each dismissal is kept in Records.
            </p>
          </Row>
          <Row label="Beep">
            <p className="text-body">Detected in the app. Plays after 35 seconds at most.</p>
          </Row>
        </ul>
      </Card>
    </div>
  );
}

function NoticeCard({ s }: { s: VmDropsAdmin }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ack = useMutation({
    mutationFn: (items: string[]) => vmDrops.acknowledge(s.notice_version, items),
    onSuccess: (v) => {
      qc.setQueryData(vmKeys.admin, v);
      setOpen(false);
      toast("Agreement recorded.");
    },
    onError: (e) => {
      toast(errorMessage(e), "danger");
      qc.invalidateQueries({ queryKey: vmKeys.admin });
    },
  });
  const a = s.acknowledgment;
  return (
    <Card
      title="Notice"
      actions={
        <Button variant={s.ack_current ? "secondary" : "tinted"} onClick={() => setOpen(true)}>
          {s.ack_current ? "Read again" : "Read and agree"}
        </Button>
      }
    >
      <div className="px-4 py-3 text-body">
        {a && s.ack_current ? (
          <p>
            <Badge tone="ok">Agreed</Badge> by {a.user_name ?? "an admin"} on {fmtDateTime(a.acknowledged_at)}
            , version <span className="tnum">{a.notice_version}</span>.
          </p>
        ) : a ? (
          <p className="text-ink-2">
            Agreed by {a.user_name ?? "an admin"} to version <span className="tnum">{a.notice_version}</span>.
            The notice changed since; read it and agree again.
          </p>
        ) : (
          <p className="text-ink-2">Not agreed yet.</p>
        )}
      </div>
      <Dialog open={open} onClose={() => setOpen(false)} title={s.notice.title} size="lg">
        <div className="flex max-h-[70vh] flex-col gap-6 overflow-y-auto pr-1">
          <NoticeText notice={s.notice} />
          <NoticeAgree
            notice={s.notice}
            version={s.notice_version}
            busy={ack.isPending}
            onConfirm={(items) => ack.mutate(items)}
          />
        </div>
      </Dialog>
    </Card>
  );
}

function NoticeText({ notice }: { notice: Notice }) {
  return (
    <article aria-label={notice.title} className="flex flex-col gap-3">
      <p className="text-body text-ink-2">{notice.intro}</p>
      {notice.paragraphs.map((p) => (
        <p key={p.slice(0, 48)} className="text-body">
          {p}
        </p>
      ))}
      {notice.sources.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {notice.sources.map((src) => (
            <li key={src.label}>
              <a
                href={src.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-accent-ink text-ui hover:underline"
              >
                {src.label}
                <ExternalLink className="size-3" aria-hidden />
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

function NoticeAgree({
  notice,
  version,
  onConfirm,
  busy,
}: {
  notice: Notice;
  version: string;
  onConfirm: (items: string[]) => void;
  busy?: boolean;
}) {
  const [ticked, setTicked] = useState<Set<string>>(() => new Set());
  const [agree, setAgree] = useState(false);
  const all = notice.items.every((i) => ticked.has(i.id)) && agree;
  const toggle = (id: string, on: boolean) =>
    setTicked((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });
  return (
    <form
      aria-label="Agree to the notice"
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (all) onConfirm(notice.items.map((i) => i.id));
      }}
    >
      <div className="flex items-center gap-2">
        <h3 className="font-medium text-title">What the company agrees to</h3>
        <Badge tone="warn">Draft, pending counsel review</Badge>
      </div>
      <ul className="flex flex-col gap-2">
        {notice.items.map((i) => (
          <li key={i.id} className="flex items-start gap-3">
            <Checkbox
              id={`vm-ack-${i.id}`}
              className="mt-0.5"
              label={i.text}
              checked={ticked.has(i.id)}
              onCheckedChange={(v) => toggle(i.id, v)}
            />
            <label htmlFor={`vm-ack-${i.id}`} className="text-body">
              {i.text}
            </label>
          </li>
        ))}
        <li className="flex items-start gap-3 border-hairline border-t pt-3">
          <Checkbox
            id="vm-ack-agreement"
            className="mt-0.5"
            label={notice.agreement}
            checked={agree}
            onCheckedChange={setAgree}
          />
          <label htmlFor="vm-ack-agreement" className="font-medium text-body">
            {notice.agreement}
          </label>
        </li>
      </ul>
      <div className="flex items-center justify-between gap-3">
        <span className="text-caption text-ink-3 tnum">Version {version}</span>
        <Button type="submit" variant="primary" disabled={!all} busy={busy}>
          Confirm and agree
        </Button>
      </div>
    </form>
  );
}

// State rules -----------------------------------------------------------------------------------

function RulesTab({ s }: { s: VmDropsAdmin }) {
  const qc = useQueryClient();
  const put = useMutation({
    // The table is shared with Parallel dialing: keep the rule's other fields as they are.
    mutationFn: async ({ state, noMessage }: { state: string; noMessage: boolean }) => {
      const full = await qc.fetchQuery({ ...pdialQuery, staleTime: 0 });
      const rule: StateRule = full.state_rules.find((r) => r.state === state) ?? {
        state,
        single_line: noMessage,
        no_message: noMessage,
        max_attempts_24h: null,
        no_sunday: false,
        no_call_dates: [],
      };
      return pdial.putStateRule({ ...rule, no_message: noMessage });
    },
    onSuccess: (v) => {
      qc.setQueryData(pdialKeys.admin, v);
      qc.invalidateQueries({ queryKey: vmKeys.admin });
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const [add, setAdd] = useState("");
  const rules = [...s.state_rules].sort(
    (a, b) => Number(b.no_message) - Number(a.no_message) || a.state.localeCompare(b.state),
  );
  return (
    <Card
      title="States"
      actions={
        <div className="flex items-center gap-2">
          <label htmlFor="vm-add-state" className="text-ui text-ink-2">
            State
          </label>
          <Input
            id="vm-add-state"
            aria-label="State to add"
            placeholder="TX"
            className="w-16 uppercase"
            maxLength={2}
            value={add}
            onChange={(e) => setAdd(e.target.value.toUpperCase())}
          />
          <Button
            size="sm"
            disabled={!/^[A-Z]{2}$/.test(add) || put.isPending}
            onClick={() => {
              put.mutate({ state: add, noMessage: true });
              setAdd("");
            }}
          >
            Add state
          </Button>
        </div>
      }
    >
      <Table aria-label="State rules for voicemail drops">
        <THead>
          <tr>
            <Th>State</Th>
            <Th>No recorded messages</Th>
          </tr>
        </THead>
        <TBody>
          {rules.map((r) => (
            <Tr key={r.state}>
              <Td className="font-medium">
                {r.state_name} <span className="text-caption text-ink-3">{r.state}</span>
              </Td>
              <Td>
                <Switch
                  label={`${r.state_name} no recorded messages`}
                  checked={r.no_message}
                  disabled={put.isPending}
                  onChange={(v) => put.mutate({ state: r.state, noMessage: v })}
                />
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
      <p className="px-4 py-3 text-caption text-ink-3">
        Shared with Parallel dialing. Drops never play in states marked No recorded messages.
      </p>
    </Card>
  );
}

// Records -------------------------------------------------------------------------------------

function RecordsTab({ s }: { s: VmDropsAdmin }) {
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const cursor = cursors[cursors.length - 1] ?? null;
  const q = useQuery(vmRecordsQuery(cursor));
  return (
    <div className="flex flex-col gap-4">
      <Card title="Last 30 days">
        {s.counts.length === 0 ? (
          <p className="px-4 py-3 text-body text-ink-2">No drops in the last 30 days.</p>
        ) : (
          <Table aria-label="Drops per rep">
            <THead>
              <tr>
                <Th>Rep</Th>
                <Th numeric>Left</Th>
                <Th numeric>Partly left</Th>
                <Th numeric>Not left</Th>
                <Th numeric>Refused</Th>
              </tr>
            </THead>
            <TBody>
              {s.counts.map((c) => (
                <Tr key={c.user_id}>
                  <Td>{c.user_name}</Td>
                  <Td numeric className="tnum">
                    {c.left}
                  </Td>
                  <Td numeric className="tnum">
                    {c.partial}
                  </Td>
                  <Td numeric className="tnum">
                    {c.not_left}
                  </Td>
                  <Td numeric className="tnum">
                    {c.refused}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
      <Card
        title="Records"
        actions={
          <a className="text-accent-ink text-ui hover:underline" href={recordsCsvHref()} download>
            Export CSV
          </a>
        }
      >
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : q.data.items.length === 0 ? (
          <p className="px-4 py-3 text-body text-ink-2">No drops or refusals yet.</p>
        ) : (
          <Table aria-label="Drop records">
            <THead>
              <tr>
                <Th>Time</Th>
                <Th>Rep</Th>
                <Th>Number</Th>
                <Th>Line</Th>
                <Th>State</Th>
                <Th>Message</Th>
                <Th>Result</Th>
                <Th>Beep</Th>
              </tr>
            </THead>
            <TBody>
              {q.data.items.map((r) => {
                const res = RESULT[r.state];
                return (
                  <Tr key={r.id}>
                    <Td className="tnum">{fmtDateTime(r.requested_at)}</Td>
                    <Td>{r.user_name}</Td>
                    <Td className="tnum">{r.number}</Td>
                    <Td>{LINE[r.line_type]}</Td>
                    <Td>{r.state_code ?? <span className="text-ink-3">Unknown</span>}</Td>
                    <Td>
                      {r.message_name ?? <span className="text-ink-3">None</span>}
                      {r.played_ms != null && r.state === "partial" ? (
                        <span className="block text-caption text-ink-3 tnum">
                          {clockMs(r.played_ms)} played
                        </span>
                      ) : null}
                    </Td>
                    <Td>
                      <span title={r.refused_reason ?? undefined}>
                        <Badge tone={res.tone}>{res.label}</Badge>
                      </span>
                      {r.state === "refused" && r.refused_reason ? (
                        <span className="block text-caption text-ink-3">{r.refused_reason}</span>
                      ) : null}
                    </Td>
                    <Td>{r.beep_source ? BEEP[r.beep_source] : ""}</Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        )}
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <p className="text-caption text-ink-3">Kept for 5 years.</p>
          <div className="flex gap-2">
            {cursors.length > 1 ? (
              <Button size="sm" variant="ghost" onClick={() => setCursors((c) => c.slice(0, -1))}>
                Newer
              </Button>
            ) : null}
            {q.data?.next_cursor ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  const next = q.data?.next_cursor;
                  if (next) setCursors((c) => [...c, next]);
                }}
              >
                Older
              </Button>
            ) : null}
          </div>
        </div>
      </Card>
      <DismissalsCard />
    </div>
  );
}

/** Who chose to use a message with warnings: who, when, which message, and the warnings. */
function DismissalsCard() {
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const cursor = cursors[cursors.length - 1] ?? null;
  const q = useQuery(vmDismissalsQuery(cursor));
  return (
    <Card title="Warnings dismissed">
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : q.data.items.length === 0 ? (
        <p className="px-4 py-3 text-body text-ink-2">No message warnings dismissed yet.</p>
      ) : (
        <Table aria-label="Dismissed warnings">
          <THead>
            <tr>
              <Th>Time</Th>
              <Th>Who</Th>
              <Th>Message</Th>
              <Th>Warnings</Th>
            </tr>
          </THead>
          <TBody>
            {q.data.items.map((d) => (
              <Tr key={d.id}>
                <Td className="tnum">{fmtDateTime(d.dismissed_at)}</Td>
                <Td>{d.dismissed_by_name}</Td>
                <Td>
                  {d.message_name}
                  {d.review_kind === "basic" ? (
                    <span className="block text-caption text-ink-3">Basic checks only</span>
                  ) : null}
                </Td>
                <Td>
                  <ul className="flex flex-col gap-1">
                    {d.warnings.map((w) => (
                      <li key={`${w.code}-${w.reason}`} className="text-body">
                        {w.reason}
                      </li>
                    ))}
                  </ul>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <p className="text-caption text-ink-3">
          The rep or manager took responsibility for the message as it was. In the CSV too. Kept for 5 years.
        </p>
        <div className="flex gap-2">
          {cursors.length > 1 ? (
            <Button size="sm" variant="ghost" onClick={() => setCursors((c) => c.slice(0, -1))}>
              Newer
            </Button>
          ) : null}
          {q.data?.next_cursor ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                const next = q.data?.next_cursor;
                if (next) setCursors((c) => [...c, next]);
              }}
            >
              Older
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
